// ============================================================
// SearchInput.tsx
// Reusable search field with a clear "×" button, extracted from the
// case-list search pattern so every search bar behaves the same:
//   - the × shows ONLY when there's text (value.length > 0),
//   - clicking it clears the text (onValueChange('')) AND refocuses the
//     input, so the parent's filter/search re-runs on the empty value
//     (returning to unfiltered results).
//
// Presentation + clear-affordance ONLY — filtering stays in each parent:
// onValueChange just updates the parent's query state, which already
// drives its own client-side filter or backend search.
//
// Sizing/placement vary per call site, so `className` is merged onto the
// <input> and `wrapperClassName` onto the relative wrapper (e.g. 'flex-1'
// for search bars that sit in a flex row). The component owns `w-full`
// on the input and the `pr-12` room-reservation for the × — callers
// should provide their LEFT padding (pl-*), not px-*, to avoid a
// right-padding conflict with pr-12 (mirrors the original case-list
// recipe: pl-4 pr-12).
// ============================================================
import React, { useRef } from 'react'

interface SearchInputProps {
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  /** Merged onto the <input> — per-call sizing / colour classes. */
  className?: string
  /** Merged onto the relative wrapper — e.g. 'flex-1' in a flex row. */
  wrapperClassName?: string
  /** Optional external ref (so a parent can focus/blur the input). */
  inputRef?: React.RefObject<HTMLInputElement | null>
  /** Passthrough — e.g. the case-list two-stage Esc handler. */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  autoComplete?: string
  autoFocus?: boolean
  ariaLabel?: string
}

const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onValueChange,
  placeholder,
  className = '',
  wrapperClassName = '',
  inputRef,
  onKeyDown,
  autoComplete,
  autoFocus,
  ariaLabel,
}) => {
  // Fall back to an internal ref when the caller doesn't supply one, so
  // the clear button can always refocus after clearing.
  const internalRef = useRef<HTMLInputElement>(null)
  const ref = inputRef ?? internalRef

  const handleClear = (): void => {
    onValueChange('')
    ref.current?.focus()
  }

  return (
    <div className={`relative ${wrapperClassName}`}>
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        className={`w-full ${className} pr-12`}
      />
      {value.length > 0 && (
        // Clear button — rendered only when there's text. min-w/h-[44px]
        // gives a comfortable touch target though the icon is 16px.
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="absolute right-1 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center text-casevo-mid hover:text-casevo-forest transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  )
}

export default SearchInput
